/**
 * Typed client for the backend.
 *
 * The browser never talks to Anthropic — only to here.
 *
 * Authentication is the session cookie set by `POST /api/auth/login`. There is no token
 * in this bundle any more: `VITE_FACILITATOR_TOKEN` was readable by anyone who loaded
 * the page, gave no per-person identity, and opened every route to whoever found it.
 * That was D5's shared token, and deleting it from here is what closed it.
 *
 * The cookie is HttpOnly, so nothing in this file can read it — which is the point. It
 * travels because every request sets `credentials: "same-origin"`, and the dev server
 * proxies /api so development is same-origin exactly as production is.
 */

import type { RunOutcome } from "./runners/types";
import type { SoundsCatalogue } from "./sounds";

// Empty by default: the API is same-origin, through Caddy in production and through
// Vite's dev proxy in development. Overridable for the smoke stack on another port.
const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export interface EditorRunPayload {
  code: string;
  outcome: RunOutcome;
  detail: string;
  language: string;
}

/**
 * How many buffered runs one turn may carry. The server rejects a longer list
 * outright, and a learner tinkering with a Strudel pattern reaches 20 runs
 * between messages without trying — so the browser drops the oldest instead of
 * sending a body it knows will be refused. Keep in step with
 * `MAX_RUNS_PER_TURN` in the API schemas.
 */
export const MAX_BUFFERED_RUNS = 20;

/** Append a run to the buffer, keeping only the newest `MAX_BUFFERED_RUNS`. */
export function bufferRun(
  previous: EditorRunPayload[],
  run: EditorRunPayload,
): EditorRunPayload[] {
  return [...previous, run].slice(-MAX_BUFFERED_RUNS);
}

export interface Concept {
  id: string;
  label: string;
  mastery: number;
  status: string;
  prereq_gaps: string[];
  required_by_project: boolean;
}

export interface SessionInfo {
  session_id: string;
  learner_code: string;
  display_name: string;
  domain_slug: string;
  domain_title: string;
  runner: string;
  has_sounds: boolean;
  session_date: string;
  session_number: number;
  onboarding_complete: boolean;
  /** What this learner had in the editor for this domain last time, or null. */
  restored_code: string | null;
  prefix_tokens_estimate: number;
  project: { title: string; description: string | null; status: string; milestones: Milestone[] } | null;
  frontier: Concept[];
  review_due: Concept[];
}

export interface Milestone {
  name: string;
  done: boolean;
}

export interface Domain {
  slug: string;
  title: string;
  runner: string;
  concept_count: number;
  has_adaptation_sheet: boolean;
}

export interface TurnUsage {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number;
  cache_creation_input_tokens: number;
  cache_hit_ratio: number;
}

export interface Delta {
  concept_id: string;
  mastery_before: number;
  mastery_after: number;
  status_before: string | null;
  status_after: string;
  evidence: string;
  is_new: boolean;
}

export interface Finding {
  severity: string;
  code: string;
  path: string;
  message: string;
}

export interface EndSessionResult {
  applied: boolean;
  attempts: number;
  summary: string;
  errors: Finding[];
  warnings: Finding[];
  deltas: Delta[];
  cost_usd: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    // The session cookie. `same-origin` rather than `include` because the API is
    // same-origin in both environments, and `include` would be a standing invitation
    // to point this at another host later and wonder why credentials leak.
    //
    // Note what is *not* here: no Authorization header. The reverse proxy claims that
    // one for the site password gate, and sending Bearer alongside it overrode the
    // browser's basic-auth credentials so every request 401'd with the right password
    // typed — see D22. A cookie sidesteps the collision entirely.
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    const detail = await response.text();
    let message = detail;
    try {
      message = (JSON.parse(detail) as { detail?: string }).detail ?? detail;
    } catch {
      // A non-JSON error body is used as-is.
    }
    throw new ApiError(response.status, message || response.statusText);
  }
  return (await response.json()) as T;
}

export interface MapConcept {
  id: string;
  label: string;
  description: string;
  stage: string;
  tier: number;
  prerequisites: string[];
  mastery: number;
  status: string;
  /** The band word this domain's graph author wrote. The UI shows this, not the number. */
  band: string;
  confidence: string;
  evidence: string | null;
  misconceptions: string[];
  times_practiced: number;
  last_seen: string | null;
  next_review: string | null;
  prereq_gaps: string[];
  unlocked: boolean;
  required_by_project: boolean;
}

export interface MapStage {
  name: string;
  concepts: MapConcept[];
}

export interface SkillMap {
  learner_code: string;
  display_name: string;
  domain_slug: string;
  domain_title: string;
  graph_version: string;
  /** Lowest first. Visual intensity keys off the INDEX, so a domain that writes its
   *  own wording still renders correctly. */
  bands: string[];
  stages: MapStage[];
  project_concepts: string[];
  total: number;
  started: number;
  review_due_now: number;
}

export interface TranscriptTurn {
  role: string;
  text: string;
  runs: EditorRunPayload[];
}

export interface Transcript {
  session_id: string;
  status: string;
  /** Everything needed to rebuild the session UI. Null only for a session the
   *  server could not reopen. */
  session: SessionInfo | null;
  turns: TranscriptTurn[];
}

export interface OpenSession {
  session_id: string;
  domain_slug: string;
  session_date: string;
  status: string;
  turn_count: number;
  summary: string | null;
  last_code: string | null;
  last_language: string | null;
}

export interface FacilitatorDomain {
  slug: string;
  started: number;
  total: number;
  sessions: number;
  last_session: string | null;
}

export interface FacilitatorLearner {
  code: string;
  name: string;
  group: string | null;
  consent_ok: boolean;
  consent_stale: boolean;
  consent_note: string;
  domains: FacilitatorDomain[];
}

export interface FacilitatorSessionRow {
  id: string;
  date: string;
  learner_code: string;
  learner_name: string;
  domain_slug: string;
  status: string;
  duration_min: number | null;
  summary: string | null;
  /** applied | rejected | none */
  update_status: string;
  warnings: number;
  errors: number;
  repaired: boolean;
  /** A facilitator re-applied a rejected update. Settled: a human has looked at it. */
  recovered: boolean;
  cost_usd: number;
  /** The most serious *unreviewed* concern the current sensitivity lets through, or null.
   *  Never the words themselves — reading those is a deliberate second click. */
  safety_concern: string | null;
}

export interface FacilitatorTurn {
  role: string;
  text: string;
  runs: EditorRunPayload[];
}

export interface FacilitatorUpdate {
  attempt: number;
  model: string;
  applied: boolean;
  errors: Finding[];
  warnings: Finding[];
  deltas: Delta[];
}

export interface FacilitatorSessionDetail {
  row: FacilitatorSessionRow;
  engagement: string | null;
  facilitator_notes: string | null;
  concepts_touched: string[];
  transcript: FacilitatorTurn[];
  updates: FacilitatorUpdate[];
  /** Every flag on this session, reviewed ones included and regardless of sensitivity:
   *  somebody deliberately opened it, so hiding what was noticed would be backwards. */
  safety_flags: SafetyFlag[];
}

export interface Proposal {
  id: string;
  learner_code: string;
  domain_slug: string;
  concept_id: string;
  label: string | null;
  why: string;
  status: string;
  created_at: string;
  session_id: string | null;
}

export interface ReapplyResult {
  ok: boolean;
  applied: boolean;
  repairs: string[];
  findings: Finding[];
  deltas: Delta[];
}

export interface CostRow {
  model: string;
  calls: number;
  fresh_in: number;
  cache_read: number;
  cache_write: number;
  out: number;
  usd: number;
}

export interface CostSummary {
  by_model: CostRow[];
  total_usd: number;
  calls_counted: number;
}

/** Who this browser is signed in as. Carries no secret — there is none to carry. */
export type Role = "learner" | "facilitator" | "admin" | "super_admin";

export interface Me {
  nickname: string;
  role: Role;
  /** Role-prefixed and shaped so it cannot be mistaken for a membership number. */
  code: string | null;
  learner_code: string | null;
  /** The sheet version a new consent attestation records against. From the server, so
   *  the form can never name a different version than the one actually written. */
  consent_sheet_version: string;
}

export interface EnrolmentState {
  /** False when no phrase is set at all, which leaves the gate open on a fresh install. */
  required: boolean;
  enrolled: boolean;
}

export interface DeviceRow {
  id: string;
  label: string | null;
  created_at: string;
  last_seen_at: string;
  last_ip: string | null;
}

export interface AccessEvent {
  action: string;
  nickname: string | null;
  code: string | null;
  ip: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface NewLearnerResult {
  learner_code: string;
  nickname: string;
  consent_sheet_version: string | null;
  consent_recorded: boolean;
  warning: string | null;
}

export interface RemovalPreview {
  learner_code: string;
  display_name: string;
  domain_slug: string | null;
  counts: Record<string, number>;
  applied: boolean;
}

export interface AdminEvent {
  at: string;
  actor: string;
  actor_role: string;
  action: string;
  subject_kind: string;
  subject: string;
  detail: Record<string, unknown>;
}

export interface SafetyFlag {
  id: string;
  source: string;
  concern: string;
  category: string | null;
  /** The learner's own words. Stored on the flag, not looked up — `messages` are meant to
   *  be purged on a retention window that would leave this pointing at nothing. */
  quote: string | null;
  note: string | null;
  created_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

export interface AccountRow {
  nickname: string;
  role: string;
  active: boolean;
  /** Role-prefixed: `L-0003`, `F-0007`, `A-0001`, `S-0001`. */
  code: string;
  organisation: string;
  /** Readable since D39 — it is the offline key to the paper register. Never a secret. */
  membership_number: string;
  learner_code: string | null;
  locked_minutes: number | null;
  last_login_at: string | null;
}

export interface LabKnob {
  key: string;
  group: string;
  label: string;
  blurb: string;
  /** now | next session | next sign-in | restart */
  lands: string;
  kind: "int" | "float" | "choice";
  minimum: number | null;
  maximum: number | null;
  choices: string[];
  default: string | number;
  value: string | number;
  changed: boolean;
}

export interface Lab {
  groups: string[];
  knobs: LabKnob[];
}

export const api = {
  // -- Tutor Laboratory (super admin) ------------------------------------- #

  lab: () => request<Lab>("/api/admin/lab"),

  setLab: (key: string, value: string | number) =>
    request<Lab>("/api/admin/lab", { method: "POST", body: JSON.stringify({ key, value }) }),

  /** Omit the key to reset everything — the panel's disaster button. */
  resetLab: (key?: string) =>
    request<Lab>("/api/admin/lab/reset", {
      method: "POST",
      body: JSON.stringify({ key: key ?? null }),
    }),

  // -- sign-in ------------------------------------------------------------ #

  login: (nickname: string, membershipNumber: string, passphrase: string) =>
    request<Me>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({
        nickname,
        membership_number: membershipNumber,
        passphrase,
      }),
    }),

  // -- device enrolment ---------------------------------------------------- #
  //
  // The gate that replaced the shared site password. Asked once per device, then never
  // again — which is the whole reason it exists in this form rather than as a prompt on
  // every page load.

  enrolmentState: () => request<EnrolmentState>("/api/auth/enrolment"),

  enrol: (phrase: string, label?: string) =>
    request<EnrolmentState>("/api/auth/enrolment", {
      method: "POST",
      body: JSON.stringify({ phrase, label: label ?? null }),
    }),

  logout: () => request<{ signed_out: boolean }>("/api/auth/logout", { method: "POST" }),

  me: () => request<Me>("/api/auth/me"),

  // -- learner management (facilitator) ----------------------------------- #

  createLearner: (body: {
    nickname: string;
    membership_number: string;
    passphrase: string;
    consent_on_file: boolean;
    code?: string;
    group?: string;
    consent_note?: string;
  }) =>
    request<NewLearnerResult>("/api/facilitator/learners", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  resetLearner: (code: string, confirm: boolean, domainSlug?: string | null) =>
    request<RemovalPreview>(`/api/facilitator/learners/${code}/reset`, {
      method: "POST",
      body: JSON.stringify({ confirm, domain_slug: domainSlug ?? null }),
    }),

  deleteLearner: (code: string, confirm: boolean) =>
    request<RemovalPreview>(`/api/facilitator/learners/${code}/delete`, {
      method: "POST",
      body: JSON.stringify({ confirm }),
    }),

  setLearnerPassphrase: (code: string, passphrase: string) =>
    request<{ passphrase_set: boolean }>(`/api/facilitator/learners/${code}/passphrase`, {
      method: "POST",
      body: JSON.stringify({ passphrase }),
    }),

  adminEvents: () => request<AdminEvent[]>("/api/facilitator/events"),

  /** Mark a safety flag read. It leaves the needs-attention list, not the record. */
  reviewSafetyFlag: (flagId: string) =>
    request<SafetyFlag>(`/api/facilitator/safety/${encodeURIComponent(flagId)}`, {
      method: "POST",
    }),

  // -- devices and access (facilitator) ------------------------------------ #

  enrolmentInfo: () =>
    request<{ set: boolean; updated_by: string | null; updated_at: string | null; devices: number }>(
      "/api/admin/enrolment",
    ),

  setEnrolmentPhrase: (phrase: string) =>
    request<{ set: boolean }>("/api/admin/enrolment", {
      method: "POST",
      body: JSON.stringify({ phrase }),
    }),

  devices: () => request<DeviceRow[]>("/api/admin/devices"),

  revokeDevice: (deviceId: string) =>
    request<{ revoked: number }>(`/api/admin/devices/${encodeURIComponent(deviceId)}`, {
      method: "DELETE",
    }),

  accessLog: () => request<AccessEvent[]>("/api/admin/access"),

  // -- accounts (admin) ---------------------------------------------------- #

  accounts: () => request<AccountRow[]>("/api/admin/accounts"),

  createAccount: (body: {
    nickname: string;
    membership_number: string;
    passphrase: string;
    role: string;
  }) =>
    request<AccountRow>("/api/admin/accounts", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** Correct a membership number. **Always sets new words too** — the number is hashed
   *  into the credential, so changing it invalidates the old hash and nothing can rebuild
   *  one without a secret. Facilitator-level, but never on an account senior to you. */
  correctMembership: (code: string, membershipNumber: string, passphrase: string) =>
    request<AccountRow>(
      `/api/admin/accounts/${encodeURIComponent(code)}/membership`,
      {
        method: "POST",
        body: JSON.stringify({
          membership_number: membershipNumber,
          passphrase,
        }),
      },
    ),

  /** Keyed on the pseudonym code — nicknames are not unique since D39. */
  changeAccount: (
    code: string,
    body: { role?: string; active?: boolean; unlock?: boolean },
  ) =>
    request<AccountRow>(`/api/admin/accounts/${encodeURIComponent(code)}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  health: () => request<{ status: string; database: string }>("/health"),

  domains: () => request<Domain[]>("/api/domains"),

  // --- the facilitator dashboard ------------------------------------------ #

  facilitatorLearners: () => request<FacilitatorLearner[]>("/api/facilitator/learners"),

  facilitatorSessions: (learnerCode?: string) =>
    request<FacilitatorSessionRow[]>(
      "/api/facilitator/sessions" +
        (learnerCode ? `?learner=${encodeURIComponent(learnerCode)}` : ""),
    ),

  facilitatorSession: (sessionId: string) =>
    request<FacilitatorSessionDetail>(
      `/api/facilitator/sessions/${encodeURIComponent(sessionId)}`,
    ),

  /** Preview with confirm=false; only confirm=true writes to the graph. */
  facilitatorReapply: (sessionId: string, confirm: boolean) =>
    request<ReapplyResult>(
      `/api/facilitator/sessions/${encodeURIComponent(sessionId)}/reapply`,
      { method: "POST", body: JSON.stringify({ confirm }) },
    ),

  facilitatorProposals: (status = "pending") =>
    request<Proposal[]>(`/api/facilitator/proposals?status=${encodeURIComponent(status)}`),

  facilitatorDecideProposal: (id: string, status: "accepted" | "rejected") =>
    request<Proposal>(`/api/facilitator/proposals/${encodeURIComponent(id)}`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  facilitatorCost: () => request<CostSummary>("/api/facilitator/cost"),

  /** The learner's whole graph with their standing in it. */
  skillMap: (learnerCode: string, domainSlug: string) =>
    request<SkillMap>(
      `/api/learners/${encodeURIComponent(learnerCode)}/map?domain=${encodeURIComponent(domainSlug)}`,
    ),

  /**
   * The tutor's opening greeting, streamed, with nothing from the learner yet.
   *
   * 409 when the session already has a conversation — a reload or a double-click, not
   * an error worth showing anyone.
   */
  async streamOpening(
    sessionId: string,
    handlers: { onDelta: (text: string) => void; onDone?: (usage: TurnUsage) => void },
  ): Promise<void> {
    const response = await fetch(`${BASE}/api/sessions/${sessionId}/opening`, {
      method: "POST",
      credentials: "same-origin",
    });
    if (response.status === 409) return;
    if (!response.ok || !response.body) {
      throw new ApiError(response.status, `opening failed: ${response.status}`);
    }
    await consumeSse(response.body, handlers);
  },

  /** Reopen a session: its transcript, plus everything needed to rebuild the UI. */
  transcript: (sessionId: string) =>
    request<Transcript>(`/api/sessions/${encodeURIComponent(sessionId)}`),

  /** Sessions this learner could pick up again, open ones first. */
  learnerSessions: (learnerCode: string, domainSlug?: string) =>
    request<OpenSession[]>(
      `/api/learners/${encodeURIComponent(learnerCode)}/sessions` +
        (domainSlug ? `?domain=${encodeURIComponent(domainSlug)}` : ""),
    ),

  /** The sounds a domain's runner can play. 404 when its runner has no fixed set. */
  sounds: (slug: string) => request<SoundsCatalogue>(`/api/domains/${slug}/sounds`),

  startSession: (learnerCode: string, domainSlug: string) =>
    request<SessionInfo>("/api/sessions", {
      method: "POST",
      body: JSON.stringify({ learner_code: learnerCode, domain_slug: domainSlug }),
    }),

  endSession: (sessionId: string, durationMin: number | null, code?: string) =>
    request<EndSessionResult>(`/api/sessions/${sessionId}/end`, {
      method: "POST",
      body: JSON.stringify({ duration_min: durationMin, code: code ?? null }),
    }),

  /**
   * Throw away a session the learner never spoke in.
   *
   * They opened a playground to look at their skill map, or picked the wrong one, and
   * left. That is not a session and must not be written down as one. The server refuses
   * with a 409 once there is a conversation in it, so this is safe to call whenever the
   * browser is unsure — but the caller that knows better should end the session properly.
   */
  abandonSession: (sessionId: string) =>
    request<{ abandoned: boolean; reason: string | null }>(`/api/sessions/${sessionId}`, {
      method: "DELETE",
    }),

  /**
   * Throw it away while the page is going away.
   *
   * The twin of `saveEditorOnExit`, and for the same reason: closing the tab is the most
   * ordinary way a look-around ends, and a plain `fetch` dies with the document. Errors
   * are swallowed because there is nobody left to show them to — and the server treats a
   * session that has already gone as a success, so a double-fire is quiet.
   */
  abandonOnExit: (sessionId: string): void => {
    void fetch(`${BASE}/api/sessions/${sessionId}`, {
      method: "DELETE",
      credentials: "same-origin",
      keepalive: true,
    }).catch(() => {});
  },

  /** Remember what is in the editor. Cheap: no model call, one upserted row. */
  saveEditor: (sessionId: string, code: string) =>
    request<{ saved: boolean }>(`/api/sessions/${sessionId}/editor`, {
      method: "PUT",
      body: JSON.stringify({ code }),
    }),

  /**
   * Save while the page is going away.
   *
   * `keepalive` lets the request outlive the document, which a normal fetch does not —
   * a tab closing mid-debounce would otherwise cancel the save and lose exactly the
   * work this feature exists to keep. `sendBeacon` would also survive and would now
   * carry the session cookie, but it cannot set a content type or be given
   * `credentials`, so `keepalive` stays.
   */
  saveEditorOnExit: (sessionId: string, code: string): void => {
    void fetch(`${BASE}/api/sessions/${sessionId}/editor`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
      keepalive: true,
    }).catch(() => {});
  },

  /**
   * Stream one turn.
   *
   * `fetch` rather than `EventSource` because EventSource cannot send an
   * Authorization header and cannot POST — and the turn body carries the buffered
   * runs, which can be several kilobytes of code.
   */
  async streamTurn(
    sessionId: string,
    body: { text: string; runs: EditorRunPayload[] },
    handlers: {
      onDelta: (text: string) => void;
      onDone?: (usage: TurnUsage) => void;
      onRefusal?: (message: string) => void;
      onError?: (message: string) => void;
    },
    signal?: AbortSignal,
  ): Promise<void> {
    const response = await fetch(`${BASE}/api/sessions/${sessionId}/turns`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok || !response.body) {
      const detail = await response.text().catch(() => response.statusText);
      throw new ApiError(response.status, detail);
    }

    await consumeSse(response.body, handlers);
  },
};

/**
 * Drain an SSE body, dispatching complete frames.
 *
 * Shared by a normal turn and the opening greeting. Frames are separated by a blank
 * line and can arrive split across chunks, so only complete ones are consumed —
 * dispatching a half-arrived frame would drop the tail of a word mid-sentence.
 */
async function consumeSse(
  body: ReadableStream<Uint8Array>,
  handlers: {
    onDelta: (text: string) => void;
    onDone?: (usage: TurnUsage) => void;
    onRefusal?: (message: string) => void;
    onError?: (message: string) => void;
  },
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let split = buffer.indexOf("\n\n");
    while (split !== -1) {
      dispatch(buffer.slice(0, split), handlers);
      buffer = buffer.slice(split + 2);
      split = buffer.indexOf("\n\n");
    }
  }
}

function dispatch(
  frame: string,
  handlers: {
    onDelta: (text: string) => void;
    onDone?: (usage: TurnUsage) => void;
    onRefusal?: (message: string) => void;
    onError?: (message: string) => void;
  },
): void {
  let event = "";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) data += line.slice(6);
  }
  if (!event || !data) return;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(data) as Record<string, unknown>;
  } catch {
    return;
  }

  switch (event) {
    case "delta":
      handlers.onDelta(String(parsed.text ?? ""));
      break;
    case "done":
      handlers.onDone?.(parsed.usage as unknown as TurnUsage);
      break;
    case "refusal":
      handlers.onRefusal?.(String(parsed.message ?? "The tutor declined that one."));
      break;
    case "error":
      handlers.onError?.(String(parsed.message ?? "Something went wrong."));
      break;
    default:
      break;
  }
}

export { BASE as API_BASE };
